import React from 'react';

import { Box, keyframes, useTheme } from '@mui/material';

const LOOP_DURATION_S = 6;
const LOOP_DURATION_DRAG_S = 3.6;

/** Vertical position of the 2px track within the lane container */
const TRACK_Y = 40;
/** Card sits just above the rail (bottom edge near the track) */
const CARRIER_TOP = TRACK_Y - 36;

const carrierKeyframes = keyframes`
  0% {
    left: 2%;
    opacity: 0;
  }
  9% {
    opacity: 1;
  }
  42% {
    left: 44%;
    opacity: 1;
  }
  56% {
    left: 50%;
    opacity: 1;
  }
  91% {
    left: 90%;
    opacity: 1;
  }
  100% {
    left: 96%;
    opacity: 0;
  }
`;

const node1LitKeyframes = keyframes`
  0%, 8% {
    opacity: 0;
  }
  9%, 30% {
    opacity: 1;
  }
  31%, 100% {
    opacity: 0;
  }
`;

const node2LitKeyframes = keyframes`
  0%, 31% {
    opacity: 0;
  }
  32%, 62% {
    opacity: 1;
  }
  63%, 100% {
    opacity: 0;
  }
`;

const node3LitKeyframes = keyframes`
  0%, 63% {
    opacity: 0;
  }
  64%, 91% {
    opacity: 1;
  }
  92%, 100% {
    opacity: 0;
  }
`;

const readGlowKeyframes = keyframes`
  0%, 44% {
    opacity: 0;
  }
  45%, 62% {
    opacity: 1;
  }
  63%, 100% {
    opacity: 0;
  }
`;

const LANE_NODES = ['FILE', 'READ', 'ORGANIZE'] as const;

const NODE_LIT_ANIMATIONS = [node1LitKeyframes, node2LitKeyframes, node3LitKeyframes];

const NODE_LABEL_RANGES = [
  { start: 9, end: 30 },
  { start: 32, end: 62 },
  { start: 64, end: 91 },
] as const;

type ProcessingLaneAnimationProps = {
  isDragOver?: boolean;
  prefersReducedMotion?: boolean;
};

const DOC_WIDTH = 46;
const DOC_HEIGHT = 58;

const DocumentCard: React.FC<{ accentColor: string }> = ({ accentColor }) => (
  <Box
    aria-hidden
    sx={{
      width: DOC_WIDTH,
      height: DOC_HEIGHT,
      flexShrink: 0,
      bgcolor: 'common.white',
      border: '1px solid',
      borderColor: 'grey.400',
      borderRadius: 0.5,
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      boxSizing: 'border-box',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: 8,
        height: 8,
        bgcolor: 'grey.100',
        borderLeft: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'grey.400',
      },
    }}
  >
    <Box
      sx={{
        width: 22,
        height: 5,
        bgcolor: accentColor,
        borderRadius: 0.25,
        mt: 1,
        ml: 1,
      }}
    />
    <Box
      sx={{
        mt: 0.75,
        ml: 1,
        mr: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.375,
      }}
    >
      {[18, 14, 16].map((widthPx) => (
        <Box
          key={widthPx}
          sx={{
            height: 3,
            width: widthPx,
            maxWidth: '100%',
            bgcolor: 'grey.300',
            borderRadius: 0.25,
          }}
        />
      ))}
    </Box>
  </Box>
);

const makeLabelColorKeyframes = (
  cobalt: string,
  inactiveColor: string,
  litStartPct: number,
  litEndPct: number,
) => keyframes`
  0%, ${litStartPct - 1}% {
    color: ${inactiveColor};
  }
  ${litStartPct}%, ${litEndPct}% {
    color: ${cobalt};
  }
  ${litEndPct + 1}%, 100% {
    color: ${inactiveColor};
  }
`;

type LaneNodeProps = {
  label: string;
  litAnimation: ReturnType<typeof keyframes>;
  labelAnimation: ReturnType<typeof keyframes>;
  animationDuration: string;
  cobalt: string;
  prefersReducedMotion: boolean;
  isReadNode: boolean;
};

const LaneNode: React.FC<LaneNodeProps> = ({
  label,
  litAnimation,
  labelAnimation,
  animationDuration,
  cobalt,
  prefersReducedMotion,
  isReadNode,
}) => {
  const dotSize = 12;
  const isStaticLit = prefersReducedMotion && isReadNode;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
      }}
    >
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          bgcolor: isStaticLit ? cobalt : 'common.white',
          border: '2px solid',
          borderColor: isStaticLit ? cobalt : 'grey.400',
          boxShadow: isStaticLit ? `0 0 0 4px ${cobalt}33` : 'none',
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        {!prefersReducedMotion && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: -2,
              borderRadius: '50%',
              bgcolor: cobalt,
              border: `2px solid ${cobalt}`,
              boxShadow: `0 0 0 4px ${cobalt}33`,
              opacity: 0,
              animation: `${litAnimation} ${animationDuration} ease-in-out infinite`,
            }}
          />
        )}
      </Box>
      <Box
        component="span"
        sx={{
          mt: 0.75,
          fontFamily: 'monospace',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          lineHeight: 1,
          color: isStaticLit ? cobalt : 'grey.500',
          ...(!prefersReducedMotion
            ? {
                animation: `${labelAnimation} ${animationDuration} ease-in-out infinite`,
              }
            : {}),
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

type LaneLayoutProps = {
  animationDuration: string;
  cobalt: string;
  accentColor: string;
  labelAnimations: ReturnType<typeof makeLabelColorKeyframes>[];
  prefersReducedMotion: boolean;
  isDragOver: boolean;
};

const LaneLayout: React.FC<LaneLayoutProps> = ({
  animationDuration,
  cobalt,
  accentColor,
  labelAnimations,
  prefersReducedMotion,
  isDragOver,
}) => {
  const durationS = isDragOver ? LOOP_DURATION_DRAG_S : LOOP_DURATION_S;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        height: 76,
        px: 1,
        mb: 1,
        mx: 'auto',
        maxWidth: 360,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: TRACK_Y,
          width: 72,
          height: 72,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          bgcolor: `${cobalt}18`,
          filter: 'blur(14px)',
          opacity: prefersReducedMotion ? 1 : 0,
          ...(!prefersReducedMotion
            ? {
                animation: `${readGlowKeyframes} ${animationDuration} ease-in-out infinite`,
              }
            : {}),
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: TRACK_Y,
          height: 2,
          bgcolor: 'grey.300',
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: CARRIER_TOP,
          left: prefersReducedMotion ? '50%' : '2%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          ...(!prefersReducedMotion
            ? {
                animation: `${carrierKeyframes} ${durationS}s ease-in-out infinite`,
              }
            : {}),
          willChange: prefersReducedMotion ? undefined : 'left, opacity',
        }}
      >
        <DocumentCard accentColor={accentColor} />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: TRACK_Y - 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        {LANE_NODES.map((label, index) => (
          <LaneNode
            key={label}
            label={label}
            litAnimation={NODE_LIT_ANIMATIONS[index]}
            labelAnimation={labelAnimations[index]}
            animationDuration={animationDuration}
            cobalt={cobalt}
            prefersReducedMotion={prefersReducedMotion}
            isReadNode={label === 'READ'}
          />
        ))}
      </Box>
    </Box>
  );
};

export const ProcessingLaneAnimation: React.FC<ProcessingLaneAnimationProps> = ({
  isDragOver = false,
  prefersReducedMotion = false,
}) => {
  const theme = useTheme();
  const cobalt = theme.palette.secondary.main;
  const accentColor = theme.palette.secondary.light;
  const inactiveLabelColor = theme.palette.grey[500];
  const durationS = isDragOver ? LOOP_DURATION_DRAG_S : LOOP_DURATION_S;
  const animationDuration = `${durationS}s`;
  const labelAnimations = NODE_LABEL_RANGES.map(({ start, end }) =>
    makeLabelColorKeyframes(cobalt, inactiveLabelColor, start, end),
  );

  return (
    <LaneLayout
      animationDuration={animationDuration}
      cobalt={cobalt}
      accentColor={accentColor}
      labelAnimations={labelAnimations}
      prefersReducedMotion={prefersReducedMotion}
      isDragOver={isDragOver}
    />
  );
};

export default ProcessingLaneAnimation;
